import { useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React, { FC, useCallback, useState } from 'react';
import { Button } from 'reactstrap';
import { v4 } from 'uuid';
import { FormNodeType, FormPageModel, FormSectionModel } from '../../../../shared/kiosk';
import { ActionButtonModal, EditableText, MaterialIcon } from '../../../components';
import { useTranslation } from '../../../translations';
import { classNames, useBoolState } from '../../../utils';
import { EditModal } from './EditModal';
import { SetPage, SetPageSection, useSetSection } from './FormElements';
import { nodeEditors } from './elements';
import { DragData } from './models';

import './PageRenderer.scss';

interface DropAreaProps {
  readonly id: string;
  readonly position: number;
}

const EditorDropArea: FC<DropAreaProps> = ({ id, position }) => {
  const { ts } = useTranslation();
  const { isOver, setNodeRef } = useDroppable({
    data: {
      sectionId: id,
      position,
    },
    id,
  });

  return (
    <div
      className={classNames(
        {
          'is-over': isOver,
        },
        'express-editor-drop-area',
        'no-children',
      )}
      ref={setNodeRef}
    >
      {ts(isOver ? 'express.release_to_add' : 'express.drag_to_start')}
    </div>
  );
};

interface SectionProps {
  readonly section: FormSectionModel;
  readonly setSection: SetPageSection;
  readonly deleteSection: (sectionId: string) => void;
}

const PageSectionEditor: FC<SectionProps> = ({ section, setSection, deleteSection }) => {
  const updateNode = useCallback(
    (newNode: FormNodeType) => {
      setSection(section.id, {
        ...section,
        nodes: section.nodes.map((node) => (node.id === newNode.id ? newNode : node)),
      });
    },
    [section, setSection],
  );

  const removeNode = useCallback(
    (deleteNode: FormNodeType) => {
      setSection(section.id, {
        ...section,
        nodes: section.nodes.filter((node) => node.id !== deleteNode.id),
      });
    },
    [section, setSection],
  );

  const { attributes, isDragging, listeners, setNodeRef, transition, transform } = useSortable({
    id: section.id,
  });

  const style = {
    transition,
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : undefined,
  };

  return (
    <div
      className={classNames(
        {
          'is-dragging': isDragging,
        },
        'express-editor-section-boundary',
      )}
      ref={setNodeRef}
      style={style}
    >
      <div className="boundary-type w-100">
        <div
          className="handlebar"
          {...attributes}
          {...listeners}
          style={{ float: 'left', cursor: 'grab' }}
        >
          <MaterialIcon name="drag_indicator" />
        </div>
        <EditSectionName deleteSection={deleteSection} section={section} setSection={setSection} />
      </div>
      <SortableNodeList
        nodes={section.nodes}
        removeNode={removeNode}
        sectionId={section.id}
        updateNode={updateNode}
      />
    </div>
  );
};

interface SortableNodeListProps {
  readonly sectionId: string;
  readonly nodes: FormNodeType[];
  readonly updateNode: (newNode: FormNodeType) => void;
  readonly removeNode: (newNode: FormNodeType) => void;
}

const SortableNodeList: FC<SortableNodeListProps> = ({
  sectionId,
  nodes,
  updateNode,
  removeNode,
}) => {
  if (nodes.length === 0) {
    return <EditorDropArea id={sectionId} position={0} />;
  }

  return (
    <div className="express-editor-section-nodes">
      <SortableContext items={nodes}>
        {nodes.map((node) => {
          return (
            <SortableSectionItem
              key={node.id}
              node={node}
              remove={removeNode}
              update={updateNode}
            />
          );
        })}
      </SortableContext>
    </div>
  );
};

interface EditSectionNameProps {
  readonly section: FormSectionModel;
  readonly setSection: SetPageSection;
  readonly deleteSection: (id: string) => void;
}

const EditSectionName: FC<EditSectionNameProps> = ({ section, setSection, deleteSection }) => {
  const [edit, setEdit] = useState(false);

  const { ts } = useTranslation();
  if (!edit) {
    return (
      <div className="section-header">
        <div>{section.name}</div>
        <MaterialIcon
          className="section-header-name-edit"
          name="edit"
          onClick={() => {
            setEdit(true);
          }}
          style={{ fontSize: '18px' }}
        />
        <Button
          onClick={() => {
            setSection(section.id, {
              ...section,
              size: section.size === 'medium' ? 'large' : 'medium',
            });
          }}
          outline
          size="sm"
        >
          {section.size === 'medium' ? 'Half Width' : 'Full Width'}
        </Button>
        <Button
          color="danger"
          onClick={() => {
            deleteSection(section.id);
          }}
          outline
          size="sm"
        >
          {ts('common.delete')}
        </Button>
      </div>
    );
  }

  return (
    <div className="section-header-edit">
      <EditableText
        onChange={(name) => {
          setSection(section.id, {
            ...section,
            name,
          });
        }}
        text={section.name}
      />
      <Button
        color="primary"
        onClick={() => {
          setEdit(false);
        }}
        size="sm"
      >
        {ts('common.save')}
      </Button>
    </div>
  );
};

interface PageProps {
  readonly page: FormPageModel;
  readonly setPage: SetPage;
}

export const EditorPageRenderer: FC<PageProps> = ({ page, setPage }) => {
  const { ts } = useTranslation();
  const setSection = useSetSection(setPage);
  const addSection = useCallback(() => {
    setPage((p) => ({
      ...p,
      sections: [
        ...p.sections,
        {
          id: v4(),
          name: 'New section',
          nodes: [],
        },
      ],
    }));
  }, [setPage]);

  const deleteSection = useCallback(
    (sectionId: string) => {
      setPage((p) => ({
        ...p,
        sections: p.sections.filter((s) => s.id !== sectionId),
      }));
    },
    [setPage],
  );

  return (
    <div className="editor-renderer-page">
      <SortableContext
        items={page.sections.map((s) => s.id)}
        strategy={verticalListSortingStrategy}
      >
        {page.sections.map((s) => (
          <PageSectionEditor
            deleteSection={deleteSection}
            key={s.id}
            section={s}
            setSection={setSection}
          />
        ))}
      </SortableContext>
      <Button onClick={addSection}>{ts('express.add_new_section')}</Button>
    </div>
  );
};

interface SortableSectionItemProps {
  readonly node: FormNodeType;
  readonly update: (node: FormNodeType) => void;
  readonly remove: (node: FormNodeType) => void;
}

const SortableSectionItem: FC<SortableSectionItemProps> = ({ node, update, remove }) => {
  const { ts } = useTranslation();
  const details = nodeEditors[node.type];
  const [modal, openModal, closeModal] = useBoolState(false);
  const [edit, startEdit, stopEdit] = useBoolState(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    data: {
      source: 'section',
      type: node.type,
      node,
    } as DragData,
    id: node.id,
  });

  const onSaveModal = useCallback(
    (newNode: FormNodeType) => {
      closeModal();
      update(newNode);
    },
    [closeModal, update],
  );

  const onRemove = useCallback(() => {
    remove(node);
  }, [node, remove]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      className={classNames(
        {
          'is-dragging': isDragging,
        },
        'editor-toolbox-item',
      )}
      id={`node-${node.id}`}
      ref={setNodeRef}
      style={style}
      {...attributes}
    >
      {modal && (
        <EditModal
          name={details.getName()}
          node={node}
          onCancel={closeModal}
          onSave={onSaveModal}
        />
      )}
      <div className="handlebar" {...listeners}>
        <MaterialIcon name="drag_indicator" />
      </div>
      <div className="icon">
        <MaterialIcon name={details.getIcon()} />
      </div>
      <div className="content">
        {edit ? (
          <div className="edit-display-name">
            <input
              onChange={(e) => {
                update({ ...node, displayName: e.target.value });
              }}
              placeholder={ts('display_name_optional')}
              type="text"
              value={node.displayName ?? ''}
            />
            <MaterialIcon name="done" onClick={stopEdit} />
          </div>
        ) : (
          <>
            <strong>
              {node.displayName ? `${node.displayName} (${details.getName()})` : details.getName()}
            </strong>{' '}
            <MaterialIcon
              className="editor-toolbox-item-name-edit"
              name="edit"
              onClick={startEdit}
              style={{ fontSize: '12px' }}
            />
          </>
        )}

        <div className="description">{details.getDescription()}</div>
      </div>
      {details.getUpdateForm && (
        <div>
          <Button id={`id-${node.id}`} onClick={openModal} outline>
            {ts('common.edit')}
          </Button>
        </div>
      )}
      <div>
        <ActionButtonModal
          actionContent="common.delete"
          buttonContent="common.delete"
          color="danger"
          id={`delete-id-${node.id}`}
          onComplete={onRemove}
          outline
          title="delete_node"
        >
          {ts('are_you_sure_you_want_to_delete_this_node')}
        </ActionButtonModal>
      </div>
    </div>
  );
};
